import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const departments = [
  { icon: '👷', name: 'Candidate Enquiries', email: 'candidates@horizonrecruitmentltd.co.uk', desc: 'Job applications, CV registration, availability updates' },
  { icon: '🏗️', name: 'Client / Employer Enquiries', email: 'clients@horizonrecruitmentltd.co.uk', desc: 'Staff requests, account management, service enquiries' },
  { icon: '📧', name: 'General Enquiries', email: 'info@horizonrecruitmentltd.co.uk', desc: 'General questions, partnerships, media' },
  { icon: '💼', name: 'Accounts & Payroll', email: 'accounts@horizonrecruitmentltd.co.uk', desc: 'Invoices, timesheets, payroll queries' },
];

const regions = [
  'London & South East',
  'South West',
  'Midlands',
  'North West',
  'North East',
  'Yorkshire & Humber',
  'East Anglia',
  'Wales',
  'Scotland',
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    howHeard: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for contacting us! We will respond within 24 hours.');
  };

  return (
    <div>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Get in touch — we're here to help with all your recruitment needs</p>
          <div className="breadcrumb">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.9)' }}>Contact</span>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section">
        <div className="container">
          <div className="grid grid-3">
            {/* Address */}
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>📍</div>
              <h4>Head Office</h4>
              <p>Horizon Recruitment Ltd<br />United Kingdom</p>
              <p style={{ marginTop: '15px', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                <strong>Office Hours</strong><br />
                Monday – Friday: 7:00am – 6:00pm<br />
                Saturday: 8:00am – 12:00pm<br />
                Sunday: Closed
              </p>
            </div>

            {/* Phone */}
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>📞</div>
              <h4>Call Us</h4>
              <p>
                <strong>Main Office:</strong><br />
                <a href="tel:+441onal" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                  Contact via form below
                </a>
              </p>
              <div style={{ marginTop: '20px', padding: '15px', background: 'var(--orange)', borderRadius: 'var(--radius)', color: 'white' }}>
                <strong>24-Hour Emergency Line</strong><br />
                For urgent staffing requirements
              </div>
            </div>

            {/* Email */}
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>✉️</div>
              <h4>Email Us</h4>
              <p>
                <a href="mailto:info@horizonrecruitmentltd.co.uk" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                  info@horizonrecruitmentltd.co.uk
                </a>
              </p>
              <p style={{ marginTop: '10px', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                We aim to respond to all enquiries within 24 hours during business days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Department Contacts</h2>
            <p>Reach the right team directly for faster assistance.</p>
          </div>
          <div className="grid grid-2">
            {departments.map((d, i) => (
              <div className="card" key={i} style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                <div style={{ fontSize: '2rem', flexShrink: 0 }}>{d.icon}</div>
                <div>
                  <h4 style={{ marginBottom: '5px' }}>{d.name}</h4>
                  <a href={`mailto:${d.email}`} style={{ color: 'var(--orange)', fontWeight: 600, fontSize: '0.95rem' }}>{d.email}</a>
                  <p style={{ fontSize: '0.9rem', marginTop: '5px', color: 'var(--gray-600)' }}>{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section">
        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="section-header">
            <h2>Send Us a Message</h2>
            <p>Fill in the form below and we'll get back to you as soon as possible.</p>
          </div>
          <form onSubmit={handleSubmit}
            style={{ background: 'var(--white)', padding: '40px', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)' }}>
            <div className="form-group">
              <label>I am a... *</label>
              <select className="form-control" name="type" value={formData.type} onChange={handleChange} required>
                <option value="">Please select...</option>
                <option value="jobseeker">Job Seeker</option>
                <option value="employer">Employer / Client</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" required />
              </div>
              <div className="form-group">
                <label>Company (if applicable)</label>
                <input type="text" className="form-control" name="company" value={formData.company} onChange={handleChange} placeholder="Company name" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email Address *</label>
                <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" required />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" className="form-control" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone number" />
              </div>
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea className="form-control" name="message" value={formData.message} onChange={handleChange}
                placeholder="How can we help you?" rows="5" required></textarea>
            </div>
            <div className="form-group">
              <label>How did you hear about us?</label>
              <select className="form-control" name="howHeard" value={formData.howHeard} onChange={handleChange}>
                <option value="">Please select...</option>
                <option>Google Search</option>
                <option>Social Media</option>
                <option>Word of Mouth</option>
                <option>Job Board</option>
                <option>Referral</option>
                <option>Other</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
              Send Message
            </button>
            <p style={{ marginTop: '15px', fontSize: '0.85rem', color: 'var(--gray-600)', textAlign: 'center' }}>
              By submitting this form you agree to our privacy policy. We will never share your information with third parties.
            </p>
          </form>
        </div>
      </section>

      {/* Regional Coverage */}
      <section className="section-dark">
        <div className="container">
          <div className="section-header">
            <h2>UK-Wide Coverage</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }}>We supply construction professionals across the United Kingdom.</p>
          </div>
          <div className="grid grid-3" style={{ maxWidth: '900px', margin: '0 auto' }}>
            {regions.map((r, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.1)',
                padding: '20px',
                borderRadius: 'var(--radius)',
                textAlign: 'center',
                color: 'var(--white)',
                fontSize: '1rem',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
                📍 {r}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <h2>Need Urgent Staffing?</h2>
          <p>Our 24-hour emergency line is available for critical construction staffing needs.</p>
          <a href="mailto:info@horizonrecruitmentltd.co.uk" className="btn btn-white">
            Email Us Now
          </a>
        </div>
      </section>
    </div>
  );
}
